import { defineStore } from 'pinia'
import { store } from '@/store'
import { cloneDeep } from 'lodash-es'
import remainingRouter from '@/router/modules/remaining'
import { flatMultiLevelRoutes, generateRoute } from '@/utils/routerHelper'
import { CACHE_KEY, useCache } from '@/hooks/web/useCache'

const { wsCache } = useCache()

/**
 * 临时调试菜单（仅本地 VITE_CHANNEL_DEBUG='true' 时注入）。
 *
 * 菜单完全由后端权限点驱动，本地没有「加一条菜单」的入口，所以开发时往
 * 「招商中心」目录下补一条子菜单指向招商看板。看板的 MENU 权限点在网关侧是停用状态、
 * 后端不下发，本地只能靠它进去。这个开关只管菜单注入，看板数据一律走真接口。
 * 生产构建不注入，看板放开后连同这段一起删掉。
 */
const DEV_CHANNEL_MENUS: AppCustomRouteRecordRaw[] = [
  {
    icon: 'ep:data-line',
    name: '招商看板（调试）',
    component: 'channel/dashboard/index',
    componentName: 'ChannelDashboardDebug',
    path: '/channel/dashboard/index',
    redirect: '',
    meta: {},
    visible: true,
    keepAlive: false
  }
]

const injectDevChannelMenus = (routes: AppCustomRouteRecordRaw[]) => {
  const group = routes.find((route) => route.name === '招商中心')
  if (!group) return
  const children = group.children ?? []
  for (const menu of DEV_CHANNEL_MENUS) {
    if (!children.some((child) => child.path === menu.path)) children.push(menu)
  }
  group.children = children
}

/**
 * 协议管理调试菜单（仅本地 VITE_AGREEMENT_DEBUG='true' 时注入）。
 *
 * 后端 identity 协议管理只有 BUTTON 权限点、未下发 MENU 行，本地联调没有菜单入口，
 * 故按 DEV_CHANNEL_MENUS 同款方式把「协议管理」挂到「组织与人员」目录下。
 * 生产构建不注入；后端补 MENU seed 后连同这段一起删掉。
 */
const DEV_AGREEMENT_MENUS: AppCustomRouteRecordRaw[] = [
  {
    icon: 'ep:document',
    name: '协议管理（调试）',
    component: 'System/agreement/index',
    componentName: 'IdentityAgreementAdmin',
    path: '/identity/agreement/index',
    redirect: '',
    meta: {},
    visible: true,
    keepAlive: false
  }
]

const injectDevAgreementMenus = (routes: AppCustomRouteRecordRaw[]) => {
  const group = routes.find((route) => route.name === '组织与人员')
  if (!group) return
  const children = group.children ?? []
  for (const menu of DEV_AGREEMENT_MENUS) {
    if (!children.some((child) => child.path === menu.path)) children.push(menu)
  }
  group.children = children
}

export interface PermissionState {
  routers: AppRouteRecordRaw[]
  addRouters: AppRouteRecordRaw[]
  menuTabRouters: AppRouteRecordRaw[]
}

export const usePermissionStore = defineStore('permission', {
  state: (): PermissionState => ({
    routers: [],
    addRouters: [],
    menuTabRouters: []
  }),
  getters: {
    getRouters(): AppRouteRecordRaw[] {
      return this.routers
    },
    getAddRouters(): AppRouteRecordRaw[] {
      return flatMultiLevelRoutes(cloneDeep(this.addRouters))
    },
    getMenuTabRouters(): AppRouteRecordRaw[] {
      return this.menuTabRouters
    }
  },
  actions: {
    async generateRoutes(): Promise<unknown> {
      return new Promise<void>(async (resolve) => {
        // 获得菜单列表，它在登录的时候，setUserInfoAction 方法中已经进行获取
        let res: AppCustomRouteRecordRaw[] = []
        if (wsCache.get(CACHE_KEY.ROLE_ROUTERS)) {
          // 深拷贝再注入，避免把调试菜单写回缓存的菜单里
          res = cloneDeep(wsCache.get(CACHE_KEY.ROLE_ROUTERS) as AppCustomRouteRecordRaw[])
        }
        if (import.meta.env.VITE_CHANNEL_DEBUG === 'true') injectDevChannelMenus(res)
        if (import.meta.env.VITE_AGREEMENT_DEBUG === 'true') injectDevAgreementMenus(res)
        const routerMap: AppRouteRecordRaw[] = generateRoute(res)
        // 动态路由，404一定要放到最后面
        this.addRouters = routerMap.concat([
          {
            path: '/:path(.*)*',
            redirect: '/404',
            name: '404Page',
            meta: {
              hidden: true,
              breadcrumb: false
            }
          }
        ])
        // 渲染菜单的所有路由
        this.routers = cloneDeep(remainingRouter).concat(routerMap)
        resolve()
      })
    },
    setMenuTabRouters(routers: AppRouteRecordRaw[]): void {
      this.menuTabRouters = routers
    }
  },
  persist: false
})

export const usePermissionStoreWithOut = () => {
  return usePermissionStore(store)
}
